<template>
  <div class="periodic-launching-form">
    <!-- Frequency selection -->
    <div class="periodic-launching-form__section">
      <ButtonGroup
        v-model="type"
        :options="triggerTypes"
        title="automatedScenarios.fields.launch"
        mode="card"
        layout="vertical"
      />
      <FieldErrors
        :key="componentFieldErrorsKey"
        :errors="formValidation"
        field="multiplebox_trigger_type"
      />
    </div>

    <!-- Days selection (only for weekly) -->
    <div
      v-if="type === '1'"
      class="periodic-launching-form__section"
    >
      <ButtonGroup
        v-model="days"
        :options="triggerDays"
        :multiple="true"
        title="automatedScenarios.fields.launchDay(s)"
        custom-class="days-selector"
      />
      <FieldErrors
        :key="componentFieldErrorsKey"
        :errors="formValidation"
        field="multiplebox_trigger_days"
      />
    </div>

    <!-- Time selection -->
    <div class="periodic-launching-form__section">
      <label class="periodic-launching-form__label">
        {{ t('automatedScenarios.fields.launchTime') }}
      </label>
      <Timepicker v-model="time" />
    </div>

    <!-- Timezone option -->
    <div class="periodic-launching-form__section">
      <ButtonGroup
        v-model="tzclients"
        :options="yesno"
        title="automatedScenarios.fields.launchInCustomerTimezone"
      />
      <FieldErrors
        :key="componentFieldErrorsKey"
        :errors="formValidation"
        field="multiplebox_trigger_timezone_clients"
      />
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  onMounted,
  PropType, ref, SetupContext, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Timepicker from '@/components/fields/Timepicker.vue';
import { ButtonGroup } from '@/components/flows/operators/shared';
import FieldErrors from '@/components/fields/partials/FieldErrors.vue';
import type { PeriodicLaunchingData } from './index';
import { createDefaultData } from './index';

export default defineComponent({
  name: 'TriggerPeriodicLaunchingForm',
  components: {
    FieldErrors,
    Timepicker,
    ButtonGroup,
  },

  props: {
    modelValue: {
      type: Object as PropType<PeriodicLaunchingData>,
      required: true,
      default() {
        return createDefaultData();
      },
    },

    formValidation: {
      type: Object,
      required: false,
      default() {
        return {};
      },
    },
  },

  emits: ['update:modelValue'],

  setup(props: { modelValue: PeriodicLaunchingData; formValidation: any }, context: SetupContext) {
    const { t } = useI18n();
    const componentFieldErrorsKey = ref(0);

    watch(() => props.formValidation, () => {
      componentFieldErrorsKey.value += 1;
    });

    const type = ref('');
    const days = ref<Array<string>>([]);
    const time = ref('');
    const tzclients = ref('');

    // eslint-disable-next-line vue/no-setup-props-destructure
    type.value = props.modelValue.multiplebox_trigger_type;
    days.value = Object.values(props.modelValue.multiplebox_trigger_days ?? {});
    // eslint-disable-next-line vue/no-setup-props-destructure
    time.value = props.modelValue.time;
    // eslint-disable-next-line vue/no-setup-props-destructure
    tzclients.value = props.modelValue.multiplebox_trigger_timezone_clients;

    const triggerTypes = [
      {
        value: '0',
        label: t('automatedScenarios.fields.launches.everyDay'),
        icon: 'fa-calendar-day',
        description: t('automatedScenarios.fields.launches.everyDayDesc') !== 'automatedScenarios.fields.launches.everyDayDesc' ? t('automatedScenarios.fields.launches.everyDayDesc') : '',
      },
      {
        value: '1',
        label: t('automatedScenarios.fields.launches.everyWeek'),
        icon: 'fa-calendar-week',
        description: t('automatedScenarios.fields.launches.everyWeekDesc') !== 'automatedScenarios.fields.launches.everyWeekDesc' ? t('automatedScenarios.fields.launches.everyWeekDesc') : '',
      },
    ];

    const triggerDays = [
      { value: 'mon', label: t('automatedScenarios.fields.launchDays.mon') },
      { value: 'tue', label: t('automatedScenarios.fields.launchDays.tue') },
      { value: 'wed', label: t('automatedScenarios.fields.launchDays.wed') },
      { value: 'thu', label: t('automatedScenarios.fields.launchDays.thu') },
      { value: 'fri', label: t('automatedScenarios.fields.launchDays.fri') },
      { value: 'sat', label: t('automatedScenarios.fields.launchDays.sat') },
      { value: 'sun', label: t('automatedScenarios.fields.launchDays.sun') },
    ];

    const yesno = [
      { value: '0', label: t('no') },
      { value: '1', label: t('yes') },
    ];



    const update = () => {
      const d = days.value.reduce((prev, c) => {
        let k: number;

        switch (c) {
          case 'mon':
            k = 0;
            break;
          case 'tue':
            k = 1;
            break;
          case 'wed':
            k = 2;
            break;
          case 'thu':
            k = 3;
            break;
          case 'fri':
            k = 4;
            break;
          case 'sat':
            k = 5;
            break;
          case 'sun':
            k = 6;
            break;
          default:
            return prev;
        }

        return {
          ...prev,
          [k]: c,
        };
      }, {});

      context.emit('update:modelValue', createDefaultData({
        multiplebox_trigger_type: type.value,
        multiplebox_trigger_days: d,
        time: time.value,
        multiplebox_trigger_timezone_clients: tzclients.value,
      }));
    };

    watch([type, days, time, tzclients], update);
    onMounted(update);

    return {
      t,
      componentFieldErrorsKey,
      type,
      days,
      time,
      tzclients,
      triggerTypes,
      triggerDays,
      yesno,

    };
  },
});
</script>

<style lang="scss" scoped>
.periodic-launching-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  &__section {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &__label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
  }

  // Optimize days selector to fit in one line
  :deep(.days-selector) {
    gap: 0.25rem;

    .flow-button-group__btn {
      padding: 0.5rem 0.25rem;
      min-width: 0; // Allow shrinking
      
      span {
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }
}
</style>
